import { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef, useMemo } from "react";
import { useAppContext } from "../../context/AppContext";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/currencyFormat";

function ManualRecurringDonationSummary() {
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [recurringDonations, setRecurringDonations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState({});
  const [role, setRole] = useState(null);
  const [donorDetails, setDonorDetails] = useState([]);
  const [view, setView] = useState("excel"); // default: show Excel section
  const [downloading, setDownloading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { state } = useAppContext();
  const [uploadRows, setUploadRows] = useState([]);
  const [selectedDonation, setSelectedDonation] = useState(null);

  const { data, loading: donorLoading } = state;

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("summary");

  const axiosAPI = useAxios();
  const receiptRef = useRef();
  const fileInputRef = useRef();

  const [formData, setFormData] = useState({
    user_id: "",
    transaction_date_and_time: "",
    amount: "",
    reasonDescription: "",
    reasonCode: "",
    tr_no: "",
    mode: "",
  });

  const removeFormData = () => {
    setFormData({
      user_id: "",
      transaction_date_and_time: "",
      amount: "",
      reasonDescription: "",
      reasonCode: "",
      tr_no: "",
      mode: "",
    });
  };

  const filteredOptions = useMemo(() => {
    return data
      .filter((user) => {
        const nameMatch = user.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
        const phoneMatch = user.phone_number
          ?.toString()
          .includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch;
      })
      .slice(0, 50)
      .map((user) => ({
        value: user.id,
        label: `${user.full_name} - ${user.phone_number || ""}`,
      }));
  }, [data, searchTerm]);

  const loadOptions = (inputValue, callback) => {
    setSearchTerm(inputValue);
    callback(filteredOptions);
  };

  useEffect(() => {
    if (activeTab === "All Transactions") {
      navigate("/user/manual-recurring-donation");
    }
    // Add other tabs if needed
  }, [activeTab, navigate]);

  useEffect(() => {
    if (formData.user_id) {
      // filteredOptions is your array of options or you can fetch full user info
      const option =
        filteredOptions.find((opt) => opt.value === formData.user_id) || null;
      setSelectedUser(option);
    } else {
      setSelectedUser(null);
    }
  }, [formData.user_id, filteredOptions]);

  const handleResetFilters = () => {
    setFilters({
      search: "",
      from_date: "",
      to_date: "",
    });
    setPage(1);
    setShouldFetch(true);
  };

  useEffect(() => {
    if (shouldFetch) {
      recurringDonation();
      setShouldFetch(false); // reset trigger
    }
  }, [shouldFetch]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null; // Reset input field
      }
    } else {
      setExcelFile(file);
    }
  };

  const handleExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);

    try {
      const response = await axiosAPI.post(
        "/api/manual-recurring-donation/upload-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      //Store the rows for displaying summary table

      const { message = "", insertedCount = 0, rows = [] } = response.data;

      setUploadRows(rows);
      const failedRows = rows.filter((r) => r.status === "fail");
      const passedRows = rows.filter((r) => r.status === "pass");

      console.log("Upload Summary:", {
        insertedCount,
        passed: passedRows.length,
        failed: failedRows.length,
      });

      //  Fix: Check if rows were even processed
      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error"
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error"
        );
        console.table(failedRows);
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${message}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning"
        );
        console.table(failedRows);
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success"
        );
      }

      recurringDonation();
      // closeModal();
      // hideModal("newGiftExcelModal");

      if (fileInputRef.current) fileInputRef.current.value = null;

      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err);

      const errorMsg =
        err?.response?.data?.message ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");

      if (err.response?.data?.error) {
        console.error("Backend Error:", err.response.data.error);
      }
    }
  };

  const [filters, setFilters] = useState({
    search: "",
    from_date: "",
    to_date: "",
  });

  // const handleFilterChange = (key, value) => {
  //   setFilters((prev) => ({ ...prev, [key]: value }));
  // };

  const handleFilterChange = (key, value) => {
    setPage(1);
    // console.log("set page: ", page);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // console.log("agency ", filters.agency);
  // console.log("From date ", filters.from_date);
  // console.log("To date ", filters.to_date);

  const buildQuery = () => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    params.append("page", page);
    return params.toString();
  };

  async function recurringDonation() {
    setLoading(true); // Start loading
    const queryString = buildQuery();

    try {
      const res = await axiosAPI.get(
        `/api/manual-recurring-donation/get-manual-recurring-donation-summary?${queryString}`
      );

      // console.log("Response", res);

      setRecurringDonations(res.data.resultsByUser);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Error fetching recurring donations:", err);
    } finally {
      setLoading(false); // Always stop loading
    }
  }

  useEffect(() => {
    recurringDonation();
  }, [page]);

  useEffect(() => {
    // const role = JSON.parse(localStorage.getItem("user_role")); // Parses string "admin" to plain string
    setRole(state?.user?.role);
  }, []);

  // Get All Donor Details
  const getDonorDetails = async () => {
    try {
      const response = await axiosAPI.get("/api/donor/get-donor-details");
      setDonorDetails(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  function fetchCurrentDateAndTime() {
    const now = new Date();

    // Manually format the date and time to "YYYY-MM-DD HH:MM"
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed, so we add 1
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    // Combine the date and time into the desired format
    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

    setFormData((prev) => ({
      ...prev,
      transaction_date_and_time: formattedDateTime,
    }));
  }

  // Set current date and time when the form loads
  useEffect(() => {
    fetchCurrentDateAndTime();
  }, []);

  // console.log("Recurring Donation is", recurringDonations);

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  useEffect(() => {
    // getNotifications();
    getDonorDetails();
  }, []);

  const downloadExcel = async (table_name) => {
    setDownloading(true); //  Start loader
    const queryString = buildQuery();

    try {
      const response = await axiosAPI.get(
        `/api/manual-recurring-donation/download-manual-recurring-excel-summary?table=${table_name}&${queryString}`, //  Pass table name in query
        {
          responseType: "blob",
        }
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}"_summary".xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); //  End loader
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: formData.user_id?.value,
      transaction_date_and_time: formData.transaction_date_and_time,
      amount: parseInt(formData.amount, 10), //  ensures INT for DB
      reason_description: formData.reasonDescription,
      reason_code: formData.reasonCode,
      tr_no: formData.tr_no,
      mode: formData.mode,
    };

    console.log(payload);

    // return;

    try {
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/manual-recurring-donation/update-donation/${currentUserId}`,
          payload
        );
        window.notifyMessage(
          "Success",
          "Donation updated successfully",
          "success"
        );
        //  Local state update kro
        setRecurringDonations((prevDonations) =>
          prevDonations.map((donation) =>
            donation.id === currentUserId
              ? {
                  ...donation,
                  user_id: formData.user_id,
                  transaction_date_and_time: formData.transaction_date_and_time,
                  amount: formData.amount,
                  reason_description: formData.reasonDescription,
                  reason_code: formData.reasonCode,
                  tr_no: formData.tr_no,
                  mode: formData.mode,
                }
              : donation
          )
        );
      } else {
        response = await axiosAPI.post(
          "/api/manual-recurring-donation/add-donation",
          payload
        );
        window.notifyMessage(
          "Success",
          "Donation added successfully",
          "success"
        );
      }
      hideModal("newUserModal");
      // getNotifications();
      recurringDonation();
      removeFormData();
      // fetchCurrentDateAndTime()

      setIsEditMode(false);
      // window.location.reload(); // Reload page to reflect changes in the notification list
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  // Edit Modal Open
  // const openEditModal = (id) => {
  //   setIsEditMode(true);

  //   // Find the notification with this ID and populate the form
  //   const selected_recurrer_donation = recurringDonations.find(
  //     (donor) => donor.id === id
  //   );

  //   setCurrentUserId(id);
  //   console.log(selected_recurrer_donation);

  //   if (selected_recurrer_donation) {
  //     const selectedUser = data.find(
  //       (user) => user.id === selected_recurrer_donation.user_id
  //     );
  //     setFormData({
  //       // user_id: selected_recurrer_donation.user_id,
  //       user_id: selectedUser
  //         ? { value: selectedUser?.id, label: selectedUser?.full_name }
  //         : null,
  //       transaction_date_and_time:
  //         selected_recurrer_donation.transaction_date_and_time,
  //       amount: selected_recurrer_donation.amount,
  //       reasonDescription: selected_recurrer_donation.reason_description,
  //       reasonCode: selected_recurrer_donation.reason_code,
  //       tr_no: selected_recurrer_donation.tr_no,
  //       mode: selected_recurrer_donation.mode,
  //     });
  //   }

  //   showModal("newUserModal");
  // };

  // console.log("Manual",recurringDonations)

  const openEditModal = async (id) => {
    console.log("id",id);
    setIsEditMode(true);

    const selected_donation = recurringDonations.find(
      (donor) => donor.donor_id === id
    );
    if (!selected_donation) return;

    const donorId = selected_donation.donor_id;
    setCurrentUserId(donorId);

    // console.log("Donor Id",donorId)

    try {
      const response = await axiosAPI.get(
        `/api/manual-recurring-donation/manual-recurring-donation-summary-by-id/${donorId}`
      );
      // console.log("Response ", response);
      setSelectedDonation(response.data.results);
      showModal("donationModal");
    } catch (error) {
      console.error("Failed to fetch donor details", error);
    }
  };

  const openUploadDonorModal = () => {
    showModal("newUserExcelModal");
  };

  const downloadPdf = () => {
    const input = receiptRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true, // Enable CORS to allow external images to be loaded
      allowTaint: true, // Allow tainting the canvas
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`DonationReceipt_${receiptDetails?.full_name || "User"}.pdf`);
    });

    setShowPdfModal(false);
    setReceiptDetails("");
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(
        `/api/notification/delete-notification/${id}`
      );
      window.notifyMessage("Success", "Deleted successfully", "success");
      // getNotifications();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger"
      );
    }
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setUploadRows("");
    setSelectedDonation([]);
    setCurrentUserId(null);
  };

  // Helper functions to manage modal visibility
  function hideModal(id) {
    const modal_el = document.querySelector("#" + id);
    const modal_obj = window.bootstrap.Modal.getInstance(modal_el);

    if (modal_obj == null) {
      return;
    }

    modal_obj.hide();
  }

  function showModal(id) {
    const modal_el = document.querySelector("#" + id);
    let modal_obj = window.bootstrap.Modal.getInstance(modal_el);

    if (modal_obj == null) {
      modal_obj = new window.bootstrap.Modal(modal_el, {
        backdrop: "static",
      });
    }

    modal_obj.show();
  }

  const openAddModal = () => {
    showModal("newUserModal");
    fetchCurrentDateAndTime();
  };

  const fetchReceiptDetails = async (id) => {
    try {
      const response = await axiosAPI.get(
        `/api/manual-recurring-donation/download-donation-receipt/${id}`
      );
      // console.log("Reponse",response)
      setReceiptDetails(response.data.user_details);
      // setNotifications(response.data.results);
      // setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const openReceiptModal = (id) => {
    // Find the notification with this ID and populate the form
    const selecteRecurrer = recurringDonations.find((donor) => donor.id === id);

    fetchReceiptDetails(id);
    setShowPdfModal(true);
    // console.log("Receipt", receiptDetails);
  };

  function modifiedTrNumber(TrNumber) {
    // Format tr_no as in addDonation
    const padded = parseInt(TrNumber, 10).toString().padStart(6, "0");
    return "056031" + padded;
  }

  return (
    <div className="container">
      <div className="page-inner">
        {role === "admin" && (
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
            <div>
              {/* Nav Tabs */}
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "All Transactions" ? "active" : ""
                    }`}
                    // onClick={() => setActiveTab("All Transactions")}
                    onClick={() => navigate("/donations")}
                  >
                    All Donations
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "summary" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("summary")}
                  >
                    Donation Summary
                  </button>
                </li>
              </ul>
            </div>{" "}
            <div>
              <button
                type="button"
                className="btn btn-success btn-round"
                // onClick={() => downloadExcel("manual_recurring_donations")}
                disabled={downloading}
                onClick={() => {
                  if (
                    filters.search ||
                    (filters.from_date && filters.to_date)
                  ) {
                    downloadExcel("manual_recurring_donations"); // Pass filters if needed
                  } else {
                    const confirmFullDownload = window.confirm(
                      "No filters applied. Do you want to download the full sheet?"
                    );
                    if (confirmFullDownload) {
                      downloadExcel("manual_recurring_donations"); //  Full sheet export
                    }
                  }
                }}
              >
                Export Donations
              </button>

              {/* <button
                type="button"
                className="btn btn-success btn-round"
                onClick={() => openUploadDonorModal()}
              >
                Upload Manual Recurring Donation
              </button>

              <button
                type="button"
                className="btn btn-dark btn-round"
                onClick={() => openAddModal()}
              >
                Add Manual Recurring Donation
              </button> */}
            </div>
          </div>
        )}

        {/* Bootstrap Modal */}
        <div
          className="modal fade"
          id="donationModal"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Donation Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={closeModal}
                />
              </div>
              <div className="modal-body ">
                {selectedDonation && selectedDonation.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle">
                      <thead className="table-dark">
                        <tr>
                          <th>#</th>
                          <th>Donor ID</th>
                          <th>Name</th>
                          <th>Amount</th>
                          <th>Transaction Date</th>
                          <th>Mode</th>
                          {/* <th>Agency</th> */}
                          <th>Transaction No</th>
                          <th>Phone</th>
                          {/* <th>Reason</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDonation.map((donation, index) => (
                          <tr key={donation.id || index}>
                            <td>{index + 1}</td>
                            <td>{donation?.donor_id}</td>
                            <td>{donation?.full_name}</td>
                            <td>₹{donation?.amount}</td>
                            <td>
                              {new Date(
                                donation?.transaction_date_and_time
                              ).toLocaleDateString()}
                            </td>
                            <td>{donation?.mode}</td>
                            {/* <td>{donation.processing_agency || "N/A"}</td> */}
                            <td>{modifiedTrNumber(donation?.tr_no)}</td>
                            {/* <td>{donation?.tr_no}</td> */}
                            <td>{donation?.phone_number || "N/A"}</td>
                            {/* <td>{donation.reason_description || "N/A"}</td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No donation records available.</p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Pdf Modal */}
        {showPdfModal && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Receipt</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPdfModal(false);
                      setReceiptDetails("");
                    }}
                  ></button>
                </div>
                <div className="modal-body pb-0" ref={receiptRef}>
                  {/* Receipt Content */}

                  <div className="row mb-3">
                    <div className="col-2">
                      <img
                        // src="https://wearedeveloper.site/test/Folk/isckonkanpur-300x288-1.png"
                        src={"/isckonkanpur-300x288-1.png"}
                        alt="ISKCON Logo"
                        className="img-fluid"
                        width="150px"
                      />
                    </div>
                    <div className="col-10 text-center">
                      <h1
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#a00",
                        }}
                      >
                        INTERNATIONAL SOCIETY FOR KRISHNA CONSCIOUSNESS (ISKCON)
                      </h1>
                      <h2
                        className="mb-0"
                        style={{ fontSize: "15px", fontWeight: "bold" }}
                      >
                        Founder Acharya: His Divine Grace A.C. Bhaktivedanta
                        Swami Srila Prabhupada
                      </h2>
                      <p style={{ fontSize: "14px", fontWeight: "bold" }}>
                        (Head Office: Hare Krishna Land, Juhu, Mumbai - 400 049)
                      </p>
                      <p className="mb-0" style={{ fontSize: "12px" }}>
                        Branch: Sri Sri Radha Madhav Mandir Mainavati Marg, Post
                        Kheora, Nawabganj Kanpur 208017. U.P. Ph: 7080007066
                      </p>
                      <p style={{ fontSize: "14px" }}>
                        Registered under Maharashtra Public Trust Act 1950, vide
                        Regn. No: F-2179(Bom). Unique Regn No. (80G):
                        AAATI0017PF20219
                      </p>
                    </div>
                  </div>

                  <hr />

                  <h2 className="text-center text-decoration-underline mb-3">
                    RECEIPT
                  </h2>

                  {/* Dynamic Receipt Details */}
                  <div className="row">
                    <div className="col-12 mb-1">
                      <p className="mb-2">
                        <strong>Name: </strong>
                        {receiptDetails.full_name}
                      </p>
                      <p className="mb-2">
                        <strong>Donation Amount: </strong>
                        {receiptDetails.amount}
                      </p>
                      <p className="mb-2">
                        <strong>Address: </strong>
                        {receiptDetails.address}
                      </p>
                      <p className="mb-2">
                        <strong>Mode Of Payment: </strong>
                        {receiptDetails.mode}
                      </p>
                      {/* <p>
                        <strong>Received with thanks from: </strong>{receiptDetails?.receiver}
                      </p> */}
                      <p className="mb-2">
                        <strong>Date: </strong>
                        {receiptDetails.date
                          ? receiptDetails.date.split(" ")[0]
                          : ""}
                      </p>
                      {receiptDetails?.tr_number && (
                        <p className="mb-2">
                          <strong>Tr Number: </strong>
                          {receiptDetails?.tr_number}
                        </p>
                      )}
                      {receiptDetails?.phone_number && (
                        <p className="mb-2">
                          <strong>Phone Number: </strong>
                          {receiptDetails?.phone_number}
                        </p>
                      )}
                      {receiptDetails?.status && (
                        <p className="mb-2">
                          <strong>Status: </strong>
                          {receiptDetails?.status}
                        </p>
                      )}
                    </div>
                  </div>
                  <hr />
                  <div className="mt-4">
                    <p className="mb-1">
                      <strong>Yours in the service of Lord Sri Krishna.</strong>
                    </p>
                    <p>
                      <em>
                        Note: This is a computer-generated receipt. No signature
                        is required.
                      </em>
                    </p>
                    <p className="text-danger font-weight-bold text-center">
                      Hare Krishna Hare Krishna Krishna Krishna Hare Hare Hare
                      Rama Hare Rama Rama Rama Hare Hare
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-success"
                    onClick={downloadPdf} // Trigger the function to download the PDF
                  >
                    Download PDF
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowPdfModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode
                    ? "Edit Manual Recurring Donation"
                    : "Add Manual Recurring Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Donor Select */}
                  <div className="mb-3">
                    <label htmlFor="user_id" className="form-label">
                      Select Donor
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={filteredOptions}
                      loadOptions={loadOptions}
                      onChange={(selectedOption) =>
                        setFormData((prev) => ({
                          ...prev,
                          user_id: selectedOption,
                        }))
                      }
                      value={formData.user_id}
                      isClearable
                      placeholder="-- Select Donor --"
                    />
                  </div>

                  {/* Date & Time */}
                  <div className="mb-3">
                    <label
                      htmlFor="transaction_date_and_time"
                      className="form-label"
                    >
                      Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="transaction_date_and_time"
                      name="transaction_date_and_time"
                      value={formData.transaction_date_and_time}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div className="mb-3">
                    <label htmlFor="amount" className="form-label">
                      Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="amount"
                      name="amount"
                      placeholder="Enter Donation Amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Reason Description */}
                  <div className="mb-3">
                    <label htmlFor="reasonDescription" className="form-label">
                      Reason Description
                    </label>
                    <textarea
                      className="form-control"
                      id="reasonDescription"
                      name="reasonDescription"
                      rows="3"
                      placeholder="Enter Reason Description"
                      value={formData.reasonDescription}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Reason Code */}
                  <div className="mb-3">
                    <label htmlFor="reasonCode" className="form-label">
                      Reason Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="reasonCode"
                      name="reasonCode"
                      placeholder="Enter Reason Code"
                      value={formData.reasonCode}
                      onChange={handleChange}
                    />
                  </div>

                  {/* TR No */}
                  <div className="mb-3">
                    <label htmlFor="tr_no" className="form-label">
                      TR No
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="tr_no"
                      name="tr_no"
                      placeholder="Enter tr number"
                      value={formData.tr_no}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Mode */}
                  <div className="mb-3">
                    <label htmlFor="mode" className="form-label">
                      Mode
                    </label>
                    <select
                      className="form-select"
                      id="mode"
                      name="mode"
                      value={formData.mode}
                      onChange={handleChange}
                    >
                      <option value="">Select mode</option>
                      <option value="NACH">NACH</option>
                      <option value="Manual">Manual</option>
                      <option value="One Time Donation">
                        One Time Donation
                      </option>
                      <option value="SI">SI</option>
                      <option value="CASH">CASH</option>
                      <option value="UPI">UPI</option>
                      <option value="Online">Online</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                  >
                    {isEditMode
                      ? "Update Manual Recurring Donation"
                      : "Add Manual Recurring Donation"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Add Donor Modal */}
        <div
          className="modal fade"
          id="newUserExcelModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div
            className={`modal-dialog ${
              view === "manual"
                ? "modal-fullscreen"
                : "modal-dialog-centered modal-dialog-scrollable modal-md"
            }`}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Donation" : "Add Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    closeModal();
                    setView("excel");
                  }}
                ></button>
              </div>

              <div className="modal-body">
                {view === "excel" && (
                  <>
                    {/* Excel Upload UI */}
                    <div className="mb-3">
                      <label htmlFor="excelUpload" className="form-label">
                        Upload Excel File (.xlsx)
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="excelUpload"
                        accept=".xlsx"
                        ref={fileInputRef}
                        onChange={handleExcelFileChange}
                      />

                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href =
                              "/samples/manual_recurring_donation_sample.xlsx";
                            link.download =
                              "manual_recurring_donation_sample.xlsx";
                            link.click();
                          }}
                        >
                          Download Sample Sheet
                        </button>
                      </div>

                      <hr />
                      <small className="form-text text-muted">
                        Mode can be in format like <strong>CASH</strong>,{" "}
                        <strong>Cheque</strong>, <strong>UPI</strong>,{" "}
                        <strong>Online</strong>, <strong>Manual</strong>,{" "}
                        <strong>One Time Donation</strong>, <strong>SI</strong>,{" "}
                        <strong>NACH</strong>
                      </small>
                      <br />

                      <button
                        className="btn btn-success mt-3"
                        onClick={handleExcelSubmit} // Submit to backend
                      >
                        Upload
                      </button>
                      {message && <div className="mt-2">{message}</div>}
                      {/* Display any messages */}

                      {uploadRows.length > 0 && (
                        <div className="mt-3">
                          <h5>Upload Summary:</h5>
                          <table className="table table-bordered table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Row</th>
                                <th>Status</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadRows.map((rowData, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{rowData?.row}</td>
                                  <td
                                    style={{
                                      color:
                                        rowData?.status === "fail"
                                          ? "red"
                                          : "green",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {rowData?.status}
                                  </td>
                                  <td>{rowData?.reason || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {downloading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="spinner-border text-success"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {/* Manual Recurring Donation Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="card-title">
                  All Manual Recurring Donation Summary (
                  {recurringDonations.length})
                </h4>

                <div className="d-flex flex-wrap align-items-end gap-3">
                  {/* Search Bar */}
                  {role === "admin" && (
                    <div
                      className="position-relative"
                      style={{ width: "320px" }}
                    >
                      <input
                        className="form-control shadow-sm px-3 py-2  border-2 w-100"
                        type="text"
                        placeholder="Enter name or phone_number or donor ID..."
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange("search", e.target.value)
                        }
                        style={{ paddingRight: "110px" }}
                      />
                    </div>
                  )}
                  {/* From Date */}
                  {/* <div className="d-flex flex-column">
                    <label htmlFor="fromDate" className="form-label mb-1">
                      From
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control"
                      value={filters.from_date || ""}
                      onChange={(e) =>
                        handleFilterChange("from_date", e.target.value)
                      }
                    />
                  </div> */}

                  {/* To Date */}
                  {/* <div className="d-flex flex-column">
                    <label htmlFor="toDate" className="form-label mb-1">
                      To
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control"
                      value={filters.to_date || ""}
                      onChange={(e) =>
                        handleFilterChange("to_date", e.target.value)
                      }
                    />
                  </div> */}

                  {/* Filter Button */}
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    style={{ height: "38px" }}
                    onClick={() => {
                      if (
                        filters.search ||
                        filters.agency ||
                        (filters.from_date && filters.to_date)
                      ) {
                        setPage(1);
                        recurringDonation();
                      } else {
                        alert("Please select both From and To dates.");
                      }
                    }}
                  >
                    Filter
                  </button>

                  {/* Reset Button */}
                  <button
                    className="btn btn-icon btn-round btn-dark py-3 px-3"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Reset Filters"
                    onClick={handleResetFilters}
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Donor ID</th>
                        <th>Full Name</th>
                        {/* <th>Mode</th> */}
                        <th>Phone Number</th>
                        <th>Monthly Amount</th>
                        <th>Mode</th>
                        <th>Deposit amount</th>
                        <th>Start Date</th>
                        <th>Expiry date</th>
                        <th>Last Transaction date</th>
                        <th>Last Transaction amount</th>

                        <th>Recent Expiry date</th>
                        <th>Status</th>
                        <tr>
                          <th>Action</th>
                          {/* {role === "admin" && (
                            <th>Action</th> // Show this if the role is "admin"
                          )} */}
                        </tr>
                      </tr>
                    </thead>
                    <tbody>
                      {loading || recurringDonations.length === 0 ? (
                        <tr>
                          <td colSpan="13" className="text-center">
                            <div
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                className="spinner-border text-success"
                                role="status"
                                style={{ width: "3rem", height: "3rem" }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : recurringDonations.length === 0 ? (
                        <tr>
                          <td colSpan="13" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        recurringDonations.map((donation) =>
                        {
                         
                          const formattedDate = new Date(
                            donation.start_date
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          const months = Math.floor(
                            donation.total_amount / donation.monthly_amount
                          );

                          const expiryDate = new Date(donation.start_date);

                          expiryDate.setMonth(expiryDate.getMonth() + months);

                          const formattedExpiryDate =
                            expiryDate.toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            });

                          const isExpired = expiryDate < new Date();

                          // Calculate recent expiry date
                          const transactionAmount =
                            Number(donation?.transaction_amount) || 0;
                          const monthlyAmountRaw = donation?.monthly_amount;

                          let recentExpireDateFormatted = "";
                          let recentExpireDate = null; // Date object
                          let isRecentExpired = false;

                          if (
                            !monthlyAmountRaw ||
                            Number(monthlyAmountRaw) === 0
                          ) {
                            // monthly_amount missing or zero
                            recentExpireDateFormatted = "";
                          } else {
                            const monthlyAmount = Number(monthlyAmountRaw);
                            const isExpiredCurrent = Math.round(
                              transactionAmount / monthlyAmount
                            );

                            recentExpireDate = new Date(
                              donation.transaction_date_and_time
                            );
                            recentExpireDate.setMonth(
                              recentExpireDate.getMonth() + isExpiredCurrent
                            );

                            const options = {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            };
                            recentExpireDateFormatted =
                              recentExpireDate.toLocaleDateString(
                                "en-GB",
                                options
                              );

                            // Compare expiry date with current date
                            isRecentExpired = recentExpireDate < new Date();
                          }

                          // Check if the message length is greater than 50 characters
                          //   const truncatedMessage =
                          //     donation.donationication_message.length > 50
                          //       ? donation.donationication_message.slice(0, 50) + "..."
                          //       : donation.donationication_message;

                          return (
                            <tr key={donation.id}>
                              <td>{donation.donor_id} </td>
                              <td>{donation.full_name}</td>
                              <td>{donation.phone_number}</td>
                              <td>{donation.monthly_amount}</td>
                              <td>{donation.mode}</td>
                              <td>{donation.total_amount}</td>
                              {/* <td>{donation.recurring_donation_owner}</td> */}
                              {/* <td>{truncatedMessage}</td> */}
                              <td>{formattedDate}</td>
                              <td
                                style={{ color: isExpired ? "red" : "inherit" }}
                              >
                                {formattedExpiryDate}
                              </td>
                              <td>{formatDate(donation?.transaction_date_and_time)}</td>
                              <td>{donation?.transaction_amount}</td>
                              <td
                                style={{
                                  color: isRecentExpired ? "red" : "inherit",
                                }}
                                >
                                {recentExpireDateFormatted}
                              </td>
                                <td>{donation?.status}</td>
                              <td>
                                {/* <button
                                  className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                  onClick={() => openReceiptModal(donation.id)}
                                >
                            
                                  <i className="fas fa-download"></i>
                                </button> */}

                                <td>
                                  {role === "admin" && (
                                    <button
                                      className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                      onClick={() => openEditModal(donation.donor_id)}
                                    >
                                      <i className="fas fa-eye"></i>
                                    </button>
                                  )}
                                </td>
                                {/* {role === "admin" && (
                                  <button
                                    className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                    onClick={() => openEditModal(donation.id)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                )} */}
                                {/* {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                    onClick={() => handleDelete(donation.id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )} */}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <nav aria-label="Page navigation">
              <ul className="pagination flex justify-content-end">
                {/* First Page */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    First
                  </button>
                </li>

                {/* Previous */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page - 1)}
                  >
                    Previous
                  </button>
                </li>

                {/* Page Number Buttons in chunks */}
                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  const startPage =
                    Math.floor((page - 1) / maxPagesToShow) * maxPagesToShow +
                    1;
                  const endPage = Math.min(
                    startPage + maxPagesToShow - 1,
                    totalPages
                  );

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <li
                        key={i}
                        className={`page-item ${page === i ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageClick(i)}
                        >
                          {i}
                        </button>
                      </li>
                    );
                  }
                  return pageNumbers;
                })()}

                {/* Next */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page + 1)}
                  >
                    Next
                  </button>
                </li>

                {/* Last Page */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManualRecurringDonationSummary;
